import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'

// The Blitz Tech Services logo, drawn as vector paths so it stays sharp at
// every size. The same paths are used in the downloadable files in
// public/brand, so keep the two in sync if the logo ever changes.
//
// Mark: a </> tile where the slash is a lightning bolt (120 x 120).
// Wordmark: BLITZ with a bolt as the I, slanted for speed (353 x 100).
// Descriptor: TECH SERVICES in a light monoline cut (418 x 33).

const MARK_CHEVRONS = 'M38 40 20 60l18 20M82 40l18 20-18 20'
const MARK_BOLT = 'M68.5 28 40.5 66h18l-7 26 28-38h-18z'

const WORDMARK_B = 'M0 0H56L70 14V38L62 48L74 60V86L60 100H0ZM24 22V39H46V22ZM24 61V78H50V61Z'
const WORDMARK_L = 'M82 0H106V78H140V100H82Z'
const WORDMARK_BOLT = 'M162 0 136 60H155.3L158 100 184 40H164.7Z'
const WORDMARK_TZ = 'M188 0H258V22H235V100H211V22H188ZM266 0H334V22L296 78H334V100H266V78L304 22H266Z'

const DESCRIPTOR = 'M0 0H20M10 0V28M54 0H36V28H54M36 14H50M90 0H75L70 5V23L75 28H90M106 0V28M126 0V28M106 14H126M184 0H169L164 5V9L169 14H179L184 19V23L179 28H164M218 0H200V28H218M200 14H214M234 28V0H249L254 5V9L249 14H234M244 14L254 28M270 0L280 28L290 0M306 0V28M342 0H327L322 5V23L327 28H342M376 0H358V28H376M358 14H372M412 0H397L392 5V9L397 14H407L412 19V23L407 28H392'

// The bolt from the wordmark on its own, in a 48 x 100 box.
export const BOLT_PATH = 'M26 0 0 60H19.3L22 100 48 40H28.7Z'

type MarkTone = 'volt' | 'ink'

interface MarkGlyphsProps {
    tone: MarkTone
}

const MarkGlyphs: FC<MarkGlyphsProps> = ({ tone }): ReactNode => {
    const isVolt = tone === 'volt'

    return (
        <>
            <rect width='120' height='120' rx='28' className={isVolt ? 'fill-volt' : 'fill-ink'} />
            <path
                d={MARK_CHEVRONS}
                fill='none'
                strokeWidth='9'
                strokeLinecap='round'
                strokeLinejoin='round'
                className={isVolt ? 'stroke-ink' : 'stroke-paper'}
            />
            <path d={MARK_BOLT} className={isVolt ? 'fill-ink' : 'fill-volt'} />
        </>
    )
}

interface WordmarkGlyphsProps {
    boltClassName: string
}

const WordmarkGlyphs: FC<WordmarkGlyphsProps> = ({ boltClassName }): ReactNode => {
    return (
        <g transform='translate(18 0) skewX(-10)'>
            <path d={WORDMARK_B} fillRule='evenodd' className='fill-current' />
            <path d={WORDMARK_L} className='fill-current' />
            <path d={WORDMARK_BOLT} className={boltClassName} />
            <path d={WORDMARK_TZ} className='fill-current' />
        </g>
    )
}

const DescriptorGlyphs: FC = (): ReactNode => {
    return (
        <g transform='translate(2.94 2.5) skewX(-10)'>
            <path
                d={DESCRIPTOR}
                fill='none'
                stroke='currentColor'
                strokeWidth='5'
                strokeLinecap='square'
                strokeLinejoin='miter'
            />
        </g>
    )
}

interface LogoMarkProps {
    tone?: MarkTone
    className?: string
}

export const LogoMark: FC<LogoMarkProps> = ({ tone = 'volt', className }): ReactNode => {
    return (
        <svg viewBox='0 0 120 120' aria-hidden='true' focusable='false' className={className}>
            <MarkGlyphs tone={tone} />
        </svg>
    )
}

interface LogoWordmarkProps {
    className?: string
    boltClassName?: string
}

export const LogoWordmark: FC<LogoWordmarkProps> = ({ className, boltClassName = 'fill-volt' }): ReactNode => {
    return (
        <svg viewBox='0 0 353 100' aria-hidden='true' focusable='false' className={className}>
            <WordmarkGlyphs boltClassName={boltClassName} />
        </svg>
    )
}

interface LogoDescriptorProps {
    className?: string
}

export const LogoDescriptor: FC<LogoDescriptorProps> = ({ className }): ReactNode => {
    return (
        <svg viewBox='0 0 418 33' aria-hidden='true' focusable='false' className={className}>
            <DescriptorGlyphs />
        </svg>
    )
}

interface LogoProps {
    // full: icon, BLITZ and TECH SERVICES. compact: icon and BLITZ only, for small spaces.
    variant?: 'full' | 'compact'
    // dark: for dark backgrounds (yellow icon, light lettering). light: for light backgrounds.
    tone?: 'dark' | 'light'
    className?: string
    label?: string
}

export const Logo: FC<LogoProps> = ({
    variant = 'full',
    tone = 'dark',
    className,
    label = 'Blitz Tech Services'
}): ReactNode => {
    const isDark = tone === 'dark'
    const isFull = variant === 'full'

    return (
        <svg
            viewBox={isFull ? '0 0 374 120' : '0 0 402 120'}
            role='img'
            aria-label={label}
            className={cn(isDark ? 'text-paper' : 'text-ink', className)}
        >
            <MarkGlyphs tone={isDark ? 'volt' : 'ink'} />

            <g transform={isFull ? 'translate(148 13.1) scale(0.64)' : 'translate(148 24) scale(0.72)'}>
                <WordmarkGlyphs boltClassName={isDark ? 'fill-volt' : 'fill-current'} />
            </g>

            {isFull && (
                <g transform='translate(148 89.1) scale(0.5396)' className={isDark ? 'text-paper/65' : 'text-ink/70'}>
                    <DescriptorGlyphs />
                </g>
            )}
        </svg>
    )
}
